import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Box, Typography, CircularProgress, Alert, Divider, TextField, Button,
  IconButton, Collapse, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem,
  ListItemText, Checkbox as MuiCheckbox, Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, TablePagination,
} from '@mui/material';
import {
  AddComment as AddCommentIcon, FileDownload as ExportIcon, Close as CloseIcon,
  OpenInNew as ExpandIcon,
  // Event-type icon set — chosen so the leading column reads at a glance
  // even before the user looks at severity/text.
  PlayCircleOutline as StartedIcon,
  CheckCircleOutline as CompletedIcon,
  ErrorOutline as FailedIcon,
  Cancel as CancelledIcon,
  AddCircleOutline as CreatedIcon,
  AssignmentInd as AssignedIcon,
  StopCircle as AbortedIcon,
  History as TimelineIcon,
  CameraAlt as ExposureIcon,
  FilterAlt as FilterChangedIcon,
  CloudUpload as UploadIcon,
  CloudDone as UploadCompleteIcon,
  Warning as WarningIcon,
  ReportProblem as IssueIcon,
  CheckCircle as ResolvedIcon,
  CenterFocusWeak as AutofocusIcon,
  Explore as GuidingIcon,
  WbCloudy as WeatherIcon,
  PowerSettingsNew as PluginIcon,
  Refresh as ReconnectedIcon,
  PauseCircleOutline as PausedIcon,
  PlayCircleFilled as ResumedIcon,
  EditNote as NoteIcon,
  Settings as ProcessingIcon,
  IosShare as TransferIcon,
  AccessTime as TimeLimitIcon,
  Notifications as NotificationIcon,
  Edit as EditedIcon,
  DriveFileMove as ProjectMovedIcon,
  Delete as LocalImageRemovedIcon,
  Science as CalibrationCompletedIcon,
  Help as DefaultEventIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';

const SEVERITY_ORDER = ['critical', 'error', 'warning', 'success', 'info', 'debug'];

const SEVERITY_COLOR = {
  critical: 'error',
  error: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
  debug: 'default',
};

// Map event_type → icon component. Anything not listed falls back to
// DefaultEventIcon. Source of truth is server's
// ObservationEventType enum (server/src/types/observationLog.ts).
const EVENT_TYPE_ICON = {
  created: CreatedIcon,
  assigned: AssignedIcon,
  started: StartedIcon,
  completed: CompletedIcon,
  failed: FailedIcon,
  cancelled: CancelledIcon,
  aborted: AbortedIcon,
  status_changed: TimelineIcon,
  exposure_started: ExposureIcon,
  exposure_completed: ExposureIcon,
  filter_changed: FilterChangedIcon,
  progress_update: TimelineIcon,
  file_uploaded: UploadIcon,
  file_upload_failed: UploadIcon,
  all_files_received: UploadCompleteIcon,
  issue_detected: IssueIcon,
  issue_resolved: ResolvedIcon,
  warning: WarningIcon,
  autofocus_started: AutofocusIcon,
  autofocus_completed: AutofocusIcon,
  guiding_started: GuidingIcon,
  guiding_stopped: GuidingIcon,
  weather_check: WeatherIcon,
  weather_unsafe: WeatherIcon,
  weather_improved: WeatherIcon,
  plugin_connected: PluginIcon,
  plugin_disconnected: PluginIcon,
  heartbeat_timeout: WarningIcon,
  reconnected: ReconnectedIcon,
  notification_sent: NotificationIcon,
  user_paused: PausedIcon,
  user_resumed: ResumedIcon,
  user_updated: EditedIcon,
  user_deleted: EditedIcon,
  project_moved: ProjectMovedIcon,
  time_limit_exceeded: TimeLimitIcon,
  processing_job_created: ProcessingIcon,
  processing_job_completed: ProcessingIcon,
  processing_job_failed: ProcessingIcon,
  external_transfer_started: TransferIcon,
  external_transfer_completed: TransferIcon,
  external_transfer_failed: FailedIcon,
  external_transfer_retry: WarningIcon,
  external_storage_paused: FailedIcon,
  local_image_removed: LocalImageRemovedIcon,
  calibration_completed: CalibrationCompletedIcon,
  calibration_skipped: WarningIcon,
  observation_processing_complete: CompletedIcon,
  exotic_reduction_submitted: ProcessingIcon,
  exotic_reduction_completed: CompletedIcon,
  exotic_reduction_failed: FailedIcon,
  note_added: NoteIcon,
  custom: DefaultEventIcon,
};

const eventTypeIconFor = (type) => EVENT_TYPE_ICON[type] || DefaultEventIcon;

const formatUtcDate = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toISOString().substring(0, 10); } catch { return ''; }
};
const formatUtcTime = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toISOString().substring(11, 19); } catch { return ''; }
};
const formatUtcStamp = (ts) => {
  if (!ts) return '';
  try {
    const iso = new Date(ts).toISOString();
    return `${iso.substring(0, 10)} ${iso.substring(11, 19)}`;
  } catch { return ''; }
};

const eventTypeLabel = (t) => (t || '').replace(/_/g, ' ');

// `standalone` renders the log as the full-page view (e.g. the
// /observations/:id/log tab): it hides the "open in new tab" action and lets
// the event table use the full viewport height instead of a fixed 600px box.
const ObservationLog = ({ observationId, standalone = false, defaultRowsPerPage = 15 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterEventTypes, setFilterEventTypes] = useState([]); // [] = all
  const [filterSeverities, setFilterSeverities] = useState([]); // [] = all
  // Timestamp sort direction. 'desc' = newest first (default).
  const [sortDir, setSortDir] = useState('desc');
  // Table pagination — newest-first so page 0 is the most recent events.
  // Rows/page defaults to 15 (detail page) or the caller-supplied value
  // (the embedded detail view uses 25).
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);

  // Export-dialog state — same pattern as AdminUserActivity.
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('text');
  const [exporting, setExporting] = useState(false);

  // Observation summary (name etc.) for the export header line.
  const [observationName, setObservationName] = useState('');

  const loadLogs = useCallback(async ({ silent = false } = {}) => {
    if (!observationId) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      // Always pull every event — log should show all, no pagination.
      const response = await apiService.getObservationLogs(observationId, { limit: 10000 });
      if (response.success) {
        const logData = response.data?.logs || response.data?.timeline || [];
        setLogs(logData);
        if (response.data?.observation_name && !observationName) {
          setObservationName(response.data.observation_name);
        }
      } else {
        setError(response.error || 'Failed to load observation logs');
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
      if (!silent) setError('Failed to load observation logs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [observationId, observationName]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Live-update on incoming WebSocket events.
  useEffect(() => {
    if (!observationId) return undefined;
    const handleLogEntry = (data) => {
      if (data.observation_id === observationId && data.entry) {
        setLogs((prev) => [...prev, data.entry]);
      }
    };
    const handleObservationUpdate = (data) => {
      const updatedId = data.observation_id || data._id || data.id;
      if (updatedId === observationId && data.type !== 'progress_update') {
        loadLogs({ silent: true });
      }
    };
    websocketService.on('observation_log_entry', handleLogEntry);
    websocketService.on('observationUpdate', handleObservationUpdate);
    return () => {
      websocketService.off('observation_log_entry', handleLogEntry);
      websocketService.off('observationUpdate', handleObservationUpdate);
    };
  }, [observationId, loadLogs]);

  // Filter helpers — empty selection means "show all".
  const filterPasses = (log) => {
    if (filterEventTypes.length > 0 && !filterEventTypes.includes(log.event_type)) return false;
    if (filterSeverities.length > 0 && !filterSeverities.includes(log.severity)) return false;
    return true;
  };

  // Default sort is newest-first; the user can flip it via the
  // Timestamp header. Apply after filter so the order is the post-
  // filter view the user sees in the table.
  const filteredLogs = [...logs]
    .filter(filterPasses)
    .sort((a, b) => {
      const at = new Date(a.timestamp).getTime();
      const bt = new Date(b.timestamp).getTime();
      return sortDir === 'asc' ? at - bt : bt - at;
    });

  const uniqueEventTypes = Array.from(new Set(logs.map((l) => l.event_type).filter(Boolean))).sort();
  const uniqueSeverities = SEVERITY_ORDER.filter((s) => logs.some((l) => l.severity === s));

  // The rows actually rendered. In the inline detail view only the current
  // page is shown; the standalone full-tab view shows every event (no
  // pagination). Filtering/sorting always operate on the full set so counts,
  // dropdowns and export stay complete.
  const pagedLogs = standalone
    ? filteredLogs
    : filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Snap back to the first page whenever the filtered set changes (filter/sort
  // edits, or a new event arrives) so the view never lands on an empty page.
  useEffect(() => {
    setPage(0);
  }, [filterEventTypes, filterSeverities, sortDir]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      setSubmittingNote(true);
      const response = await apiService.addObservationNote(observationId, noteText.trim());
      if (response.success) {
        setNoteText('');
        setShowAddNote(false);
        await loadLogs();
      } else {
        alert(`Failed to add note: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to add note:', err);
      alert('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  // ---------- Export ----------
  const exportRowFromEvent = (e) => ({
    'Event Type': e.event_type || '',
    Severity: e.severity || '',
    Date: formatUtcDate(e.timestamp),
    Time: formatUtcTime(e.timestamp),
    'Event Text': e.message || '',
  });

  const triggerDownload = (content, mime, filename) => {
    const blob = new Blob([content], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const formatTextExport = (rows, headerLine) => {
    const cols = ['Event Type', 'Severity', 'Date', 'Time', 'Event Text'];
    const widths = cols.reduce((acc, c) => {
      acc[c] = Math.max(c.length, ...rows.map((r) => String(r[c] || '').length));
      return acc;
    }, {});
    const pad = (s, w) => String(s).padEnd(w, ' ');
    const lines = [];
    lines.push(headerLine);
    lines.push('');
    lines.push(cols.map((c) => pad(c, widths[c])).join('  '));
    lines.push(cols.map((c) => '-'.repeat(widths[c])).join('  '));
    rows.forEach((r) => {
      lines.push(cols.map((c) => pad(r[c] || '', widths[c])).join('  '));
    });
    return `${lines.join('\n')}\n`;
  };

  const formatCsvExport = (rows) => {
    const cols = ['Event Type', 'Severity', 'Date', 'Time', 'Event Text'];
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(',')];
    rows.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(',')));
    return `${lines.join('\n')}\n`;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Export uses the current filter set — same set the user is
      // looking at — but the underlying logs array is already
      // unpaginated, so no extra fetch needed.
      const rows = filteredLogs.map(exportRowFromEvent);
      const stamp = new Date().toISOString().split('T')[0];
      const safeId = String(observationId || 'observation').replace(/[^A-Za-z0-9._-]+/g, '_');
      const baseName = `obs-${safeId}-events-${stamp}`;
      const headerLine = `Observation Event Log - ${observationName || `Observation ${observationId}`}`;
      if (exportFormat === 'text') {
        triggerDownload(formatTextExport(rows, headerLine), 'text/plain', `${baseName}.txt`);
      } else if (exportFormat === 'csv') {
        triggerDownload(formatCsvExport(rows), 'text/csv', `${baseName}.csv`);
      } else {
        triggerDownload(JSON.stringify(rows, null, 2), 'application/json', `${baseName}.json`);
      }
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export observation log');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Collapse in={showAddNote}>
        <Box mb={2} p={2} bgcolor="background.default" borderRadius={1}>
          <TextField
            fullWidth multiline rows={3}
            placeholder="Add a note about this observation..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={submittingNote}
          />
          <Box mt={1} display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={() => { setShowAddNote(false); setNoteText(''); }} disabled={submittingNote}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleAddNote} disabled={submittingNote || !noteText.trim()}>
              {submittingNote ? 'Adding...' : 'Add Note'}
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Filters: multi-select with checkboxes for both Event Type
          and Severity. Empty selection means "all". */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Event Type</InputLabel>
          <Select
            multiple
            value={filterEventTypes}
            onChange={(e) => {
              const v = e.target.value;
              setFilterEventTypes(typeof v === 'string' ? v.split(',') : v);
            }}
            label="Event Type"
            renderValue={(selected) => (selected.length === 0 ? 'All' : `${selected.length} selected`)}
          >
            {uniqueEventTypes.map((type) => (
              <MenuItem key={type} value={type}>
                <MuiCheckbox checked={filterEventTypes.indexOf(type) > -1} />
                <ListItemText primary={eventTypeLabel(type)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            multiple
            value={filterSeverities}
            onChange={(e) => {
              const v = e.target.value;
              setFilterSeverities(typeof v === 'string' ? v.split(',') : v);
            }}
            label="Severity"
            renderValue={(selected) => (selected.length === 0 ? 'All' : `${selected.length} selected`)}
          >
            {uniqueSeverities.map((s) => (
              <MenuItem key={s} value={s}>
                <MuiCheckbox checked={filterSeverities.indexOf(s) > -1} />
                <ListItemText primary={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredLogs.length} event{filteredLogs.length !== 1 ? 's' : ''}
          </Typography>
          <Button
            variant="outlined" size="small"
            startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
            onClick={() => setExportDialogOpen(true)}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
          >
            Export
          </Button>
          <IconButton
            size="small"
            onClick={() => setShowAddNote(!showAddNote)}
            color={showAddNote ? 'primary' : 'default'}
            sx={{ p: 0.25 }}
            title="Add comment"
          >
            <AddCommentIcon fontSize="small" />
          </IconButton>
          {!standalone && (
            <IconButton
              size="small"
              component="a"
              href={`/observations/${observationId}/log`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ p: 0.25 }}
              title="Open full event log in a new tab"
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {filteredLogs.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography color="text.secondary">No events to display</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: standalone ? 'calc(100vh - 280px)' : 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 24, p: 0, py: 0.25 }} />
                <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: 70, px: 0.5 }}>Severity</TableCell>
                <TableCell
                  sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5, width: 150, whiteSpace: 'nowrap' }}
                  sortDirection={sortDir}
                >
                  <TableSortLabel
                    active
                    direction={sortDir}
                    onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                  >
                    Timestamp (UTC)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, py: 0.5 }}>Event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedLogs.map((log, idx) => {
                const Icon = eventTypeIconFor(log.event_type);
                const sevColor = SEVERITY_COLOR[log.severity] || 'default';
                return (
                  <TableRow
                    key={log.event_id || idx}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDetailEvent(log)}
                  >
                    <TableCell sx={{ width: 24, p: 0, py: 0.25 }}>
                      <Icon sx={{ fontSize: 16, color: 'text.secondary' }} titleAccess={eventTypeLabel(log.event_type)} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 0.5 }}>
                      {log.severity && (
                        <Chip
                          label={log.severity}
                          size="small"
                          color={sevColor}
                          sx={{ fontSize: '0.55rem', height: 16, textTransform: 'uppercase', px: 0.25 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {formatUtcStamp(log.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      {log.message || ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {!standalone && filteredLogs.length > 0 && (
        <TablePagination
          component="div"
          count={filteredLogs.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[15, 25, 50, 100]}
        />
      )}

      {/* Detail dialog — opens on row click; shows every field on the
          event log entry, plus the JSON details blob if present. */}
      <Dialog open={!!detailEvent} onClose={() => setDetailEvent(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Event Details
          </Typography>
          <IconButton size="small" onClick={() => setDetailEvent(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailEvent && (() => {
            const e = detailEvent;
            const fields = [
              ['Event Type', eventTypeLabel(e.event_type)],
              ['Severity', e.severity || '—'],
              ['Timestamp', e.timestamp ? new Date(e.timestamp).toISOString() : '—'],
              ['Date (UTC)', formatUtcDate(e.timestamp) || '—'],
              ['Time (UTC)', formatUtcTime(e.timestamp) || '—'],
              ['Message', e.message || '—'],
              ['Actor', e.actor ? `${e.actor.type || '?'}:${e.actor.name || e.actor.id || '?'}` : '—'],
              ['Event ID', e.event_id || '—'],
            ];
            return (
              <Box>
                <Table size="small">
                  <TableBody>
                    {fields.map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 140, color: 'text.secondary' }}>
                          {k}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>
                          {v}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {e.details && Object.keys(e.details).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Details</Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 1.5, bgcolor: 'action.hover', borderRadius: 1,
                        fontFamily: 'monospace', fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        maxHeight: 320, overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(e.details, null, 2)}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailEvent(null)} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export dialog — text / csv / json toggle. Mirrors
          AdminUserActivity export. */}
      <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>Export event log</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose a format. The export uses the current filter set (event
            type, severity).
          </Typography>
          <ToggleButtonGroup
            value={exportFormat}
            exclusive
            onChange={(_, v) => { if (v) setExportFormat(v); }}
            size="small"
            fullWidth
          >
            <ToggleButton value="text" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Text</ToggleButton>
            <ToggleButton value="csv" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>CSV</ToggleButton>
            <ToggleButton value="json" sx={{ textTransform: 'none', fontSize: '0.75rem' }}>JSON</ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting} size="small">Cancel</Button>
          <Button onClick={handleExport} disabled={exporting} variant="contained" size="small">
            {exporting ? <CircularProgress size={16} /> : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ObservationLog;
